import type { ChronicleError, ChronicleErrorCode } from '@/tauri/chronicle/types';
import { HardwareDeviceBoundElsewhereError } from './hardwareDeviceErrors';

export interface ParsedConnectionError {
  code: ChronicleErrorCode;
  /** Translation key for the error message */
  messageKey: string;
  /** Translation key for the guidance text */
  guidanceKey: string;
}

/**
 * Error pattern → parsed result mapping.
 * Each entry maps a substring (or array of substrings) to match against the error
 * message, plus the resulting code and translation key suffix.
 */
const ERROR_PATTERNS: ReadonlyArray<{
  patterns: readonly string[];
  code: ChronicleErrorCode;
  keySuffix: string;
}> = [
  {
    patterns: ['BLUETOOTH_PERMISSION_DENIED', 'BLUETOOTH_RESTRICTED'],
    code: 'BLUETOOTH_PERMISSION_DENIED',
    keySuffix: 'bluetoothPermission',
  },
  {
    patterns: ['CHRONICLE_BLUETOOTH_DISABLED', 'bluetooth disabled'],
    code: 'BLUETOOTH_DISABLED',
    keySuffix: 'bluetoothOff',
  },
  {
    patterns: ['no_devices_found', 'device not found', 'DEVICE_NOT_FOUND'],
    code: 'DEVICE_NOT_FOUND',
    keySuffix: 'notFound',
  },
  {
    patterns: ['binding_code_required'],
    code: 'CONNECTION_FAILED',
    keySuffix: 'connectionFailed',
  },
  {
    patterns: ['binding code mismatch'],
    code: 'BINDING_CODE_MISMATCH',
    keySuffix: 'bindingCodeMismatch',
  },
  {
    patterns: ['NO_BLE_ADAPTER_FOUND'],
    code: 'BLUETOOTH_ADAPTER_NOT_FOUND',
    keySuffix: 'bluetoothAdapter',
  },
  {
    patterns: ['BLE_CONNECT_TIMEOUT', 'BLE_DISCOVER_SERVICES_TIMEOUT'],
    code: 'CONNECTION_TIMEOUT',
    keySuffix: 'connectionTimeout',
  },
  {
    patterns: ['DEVICE_LINK_FAILED'],
    code: 'DEVICE_LINK_FAILED',
    keySuffix: 'linkFailed',
  },
  // Last: the generic Rust connection code. `ChronicleError::Connection`
  // wraps more specific messages (e.g. BLE_CONNECT_TIMEOUT) that the rows
  // above must classify first.
  {
    patterns: ['CHRONICLE_CONNECTION_ERROR'],
    code: 'CONNECTION_FAILED',
    keySuffix: 'connectionFailed',
  },
];

const ERROR_KEY_PREFIX = 'settings.chronicle.error';

function matchErrorPattern(errorMessage: string): ParsedConnectionError {
  for (const { patterns, code, keySuffix } of ERROR_PATTERNS) {
    if (patterns.some((p) => errorMessage.includes(p))) {
      return {
        code,
        messageKey: `${ERROR_KEY_PREFIX}.${keySuffix}Title`,
        guidanceKey: `${ERROR_KEY_PREFIX}.${keySuffix}Description`,
      };
    }
  }

  return {
    code: 'UNKNOWN',
    messageKey: `${ERROR_KEY_PREFIX}.unknownTitle`,
    guidanceKey: `${ERROR_KEY_PREFIX}.unknownDescription`,
  };
}

/**
 * Parse connection errors into user-friendly error objects.
 * Handles device not found, binding code issues, keychain access, and generic errors.
 * Returns translation keys that should be translated in the UI.
 */
export function parseConnectionError(error: unknown): ParsedConnectionError {
  // Device bound to another user (403 from backend)
  if (error instanceof HardwareDeviceBoundElsewhereError) {
    return {
      code: 'DEVICE_BOUND_ELSEWHERE',
      messageKey: `${ERROR_KEY_PREFIX}.deviceBoundElsewhereTitle`,
      guidanceKey: `${ERROR_KEY_PREFIX}.deviceBoundElsewhereDescription`,
    };
  }

  const errorMessage =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';

  // The Chronicle bridge rejects with a structured `errorCode`
  // (SCREAMING_SNAKE, e.g. CHRONICLE_BLUETOOTH_DISABLED) — match on it too,
  // not only on the display message, so Rust-coded failures don't fall
  // through to UNKNOWN.
  const errorCode =
    error instanceof Error &&
    'errorCode' in error &&
    typeof (error as { errorCode?: unknown }).errorCode === 'string'
      ? (error as { errorCode: string }).errorCode
      : '';

  return matchErrorPattern(`${errorCode} ${errorMessage}`);
}

/**
 * Convert a ParsedConnectionError to a ChronicleError.
 * The message and guidance are translation keys that should be translated in the UI.
 */
export function toChronicleError(parsed: ParsedConnectionError): ChronicleError {
  return {
    code: parsed.code,
    message: parsed.messageKey,
    recoverable:
      parsed.code !== 'DEVICE_BOUND_ELSEWHERE' && parsed.code !== 'BINDING_CODE_MISMATCH',
    guidance: parsed.guidanceKey,
  };
}
